import React from 'react';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';
import { FileText, Download, Trash2, Calendar, HardDrive, Pencil, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';

const Documents = () => {
  const { documents, deleteDocument, renameDocument } = useApp();

  const handleDelete = async (documentId, filename) => {
    if (window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      try {
        await deleteDocument(documentId);
        toast.success('Document deleted successfully');
      } catch (error) {
        toast.error('Failed to delete document');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (value) => {
    if (!value) return 'Unknown';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'Unknown';
    return d.toLocaleDateString();
  };

  const handleRename = async (documentId, currentFilename) => {
    const nextName = window.prompt('Rename file', currentFilename || '');
    if (nextName === null) return;
    const trimmed = nextName.trim();
    if (!trimmed) {
      toast.error('Filename cannot be empty');
      return;
    }

    try {
      await renameDocument(documentId, trimmed);
      toast.success('Document renamed successfully');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to rename document');
    }
  };

  const handleAutoSchedule = async (documentId) => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const res = await axios.post(`/api/documents/${documentId}/auto-schedule`, {
        timezone: tz,
        deliver_telegram: false,
      });
      const count = res?.data?.created_tasks || 0;
      toast.success(count > 0 ? `Created ${count} scheduled task(s)` : 'No tasks detected in document');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to auto-schedule tasks');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">My Documents</h1>
        <p className="text-gray-600 mt-2">
          Manage and organize your uploaded documents.
        </p>
      </div>

      {/* Documents Grid */}
      {documents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc) => (
            <div key={doc.id} className="card hover:shadow-md transition-shadow duration-300">
              {/* Document Icon and Title */}
              <div className="flex items-start space-x-3 mb-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {doc.filename}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {doc.file_type?.toUpperCase() || 'Unknown'}
                  </p>
                </div>
              </div>

              {/* Document Metadata */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <HardDrive className="h-4 w-4 mr-2" />
                  <span>{formatFileSize(doc.file_size || 0)}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>
                    Uploaded {formatDate(doc.upload_date || doc.created_at)}
                  </span>
                </div>
              </div>

              {/* Document Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    // TODO: Implement download functionality
                    toast.info('Download feature coming soon');
                  }}
                  className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleAutoSchedule(doc.id)}
                    className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700"
                  >
                    <Wand2 className="h-4 w-4" />
                    <span>Auto-schedule</span>
                  </button>

                  <button
                    onClick={() => handleRename(doc.id, doc.filename)}
                    className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700"
                  >
                    <Pencil className="h-4 w-4" />
                    <span>Rename</span>
                  </button>

                  <button
                    onClick={() => handleDelete(doc.id, doc.filename)}
                    className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No documents uploaded yet
          </h3>
          <p className="text-gray-600 mb-6">
            Upload your first document to get started with AI-powered analysis.
          </p>
          <a href="/upload" className="btn-primary">
            Upload Document
          </a>
        </div>
      )}
    </div>
  );
};

export default Documents;
